import React, { useEffect, useState } from 'react'
import { Input } from 'semantic-ui-react'
import styled from 'styled-components'
import { useTabletAndBelowMediaQuery } from 'layout'
import { useAccounts, useCrmLookups, usePreference } from 'hooks/crm'
import { useDebounce } from 'hooks/debounce'
import { AccountDetail } from './AccountDetail'
import { AccountFormModal } from './AccountFormModal'
import { AccountList } from './AccountList'
import { CrmDrawer } from './CrmOverlay'
import { DealFormModal } from './DealFormModal'
import { AsyncState, Card, Row } from './ui'

const Layout = styled.div`
    display: grid;
    grid-template-columns: minmax(280px, 360px) minmax(0, 1fr);
    gap: 16px;
    align-items: start;
    @media (max-width: 991px) {
        grid-template-columns: 1fr;
    }
`

const select = { padding: 8, minWidth: 0, flex: 1 }

/**
 * Accounts & health: accounts sorted by risk (or name) with a detail pane. On phones the detail
 * opens full screen.
 * @param {{ openAccountId: number|null, onOpenAccount: (id: number|null, replace?: boolean) => void,
 *   onOpenContact: (id: number) => void, onOpenDeal: (id: number) => void }} props
 */
export const AccountsView = ({ openAccountId, onOpenAccount, onOpenContact, onOpenDeal }) => {
    const isMobile = useTabletAndBelowMediaQuery()
    const { lookups } = useCrmLookups()
    const [sort, setSort] = usePreference('accounts.sort', 'risk')
    const [searchText, setSearchText] = useState('')
    const search = useDebounce(searchText.trim(), 300)
    const [type, setType] = useState('')
    const [owner, setOwner] = useState('')
    const [editing, setEditing] = useState(null)
    const [dealFor, setDealFor] = useState(null)

    const accounts = useAccounts({ sort, search: search || undefined, type: type || undefined, ownerUserId: owner === '' ? undefined : Number(owner) })
    const list = accounts.data || []

    // On desktop the detail pane is never empty: show the first account until one is chosen.
    useEffect(() => {
        if (!isMobile && !openAccountId && list.length > 0) onOpenAccount(list[0].AccountId, true)
        // eslint-disable-next-line
    }, [isMobile, openAccountId, list.length])

    const detail = openAccountId ? (
        <AccountDetail
            accountId={openAccountId}
            onEdit={setEditing}
            onDeleted={() => onOpenAccount(null)}
            onOpenContact={onOpenContact}
            onOpenDeal={onOpenDeal}
            onNewDeal={account => setDealFor(account)}
        />
    ) : null

    return (
        <div>
            <Layout>
                <Card style={{ padding: 12 }}>
                    <Row $between $wrap style={{ marginBottom: 8 }}>
                        <h3 style={{ margin: 0 }}>Accounts</h3>
                        <select aria-label="Sort accounts" value={sort} onChange={e => setSort(e.target.value)} style={{ padding: 6 }}>
                            <option value="risk">Most at risk first</option>
                            <option value="name">Name A–Z</option>
                        </select>
                    </Row>
                    <Input fluid icon="search" iconPosition="left" placeholder="Find an account…" value={searchText} onChange={(_, d) => setSearchText(d.value)} aria-label="Find an account" />
                    <Row $gap={8} style={{ margin: '8px 0' }}>
                        <select aria-label="Account type" value={type} onChange={e => setType(e.target.value)} style={select}>
                            <option value="">Any type</option>
                            {(lookups?.AccountTypes || []).map(t => (
                                <option key={t} value={t}>
                                    {t}
                                </option>
                            ))}
                        </select>
                        <select aria-label="Account owner" value={owner} onChange={e => setOwner(e.target.value)} style={select}>
                            <option value="">Any owner</option>
                            {(lookups?.Users || []).map(u => (
                                <option key={u.UserId} value={u.UserId}>
                                    {u.FullName}
                                </option>
                            ))}
                        </select>
                    </Row>
                    <AsyncState
                        loading={accounts.loading}
                        error={accounts.error}
                        hasData={Boolean(accounts.data)}
                        empty={Boolean(accounts.data) && list.length === 0}
                        emptyText={search || type || owner ? 'No accounts match.' : 'No accounts yet. Add a contact with a new company to create one.'}
                        onRetry={accounts.refetch}
                    >
                        <AccountList accounts={list} activeId={openAccountId} onSelect={id => onOpenAccount(id)} />
                    </AsyncState>
                </Card>

                {!isMobile ? <Card>{detail || <span>Select an account to see its health.</span>}</Card> : null}
            </Layout>

            {isMobile ? (
                <CrmDrawer open={Boolean(openAccountId)} onClose={() => onOpenAccount(null)} title="Account" width={640}>
                    {detail}
                </CrmDrawer>
            ) : null}

            <AccountFormModal account={editing} onClose={() => setEditing(null)} />
            <DealFormModal open={Boolean(dealFor)} defaults={dealFor ? { AccountId: dealFor.AccountId } : {}} onClose={() => setDealFor(null)} onSaved={() => {}} />
        </div>
    )
}
